import { useEffect, useRef, useState } from "react";
import type { WalkPoint } from "../WalkFeedback";

// One cursor mechanic for every "Mluv a ukazuj" surface. Direct DOM writes
// keep the orb smooth without a React render for every pointer sample.
export function moveWalkCursorOrb(
  element: HTMLDivElement | null,
  active: boolean,
  clientX: number,
  clientY: number
): void {
  if (!element) return;
  if (!active) {
    element.style.opacity = "0";
    return;
  }
  element.style.transform = `translate(${clientX}px, ${clientY}px)`;
  element.style.opacity = "1";
}

// Pointer-capture layer over a delivered page in an iframe while a walk
// recording runs. Wheel and touch-drag scrolling forward into the page:
// pages built with the injected walk-scroll listener honour the postMessage;
// for pages delivered BEFORE the injector shipped, the layer briefly lets
// wheel events through so the iframe scrolls natively (finding 7).
export function WalkCaptureLayer(props: {
  frameRef: React.RefObject<HTMLIFrameElement | null>;
  onPoint: (point: Omit<WalkPoint, "t">) => void;
}) {
  const touchY = useRef<number | null>(null);
  const [passthrough, setPassthrough] = useState(false);
  const passTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(passTimer.current), []);
  const relayScroll = (deltaY: number) => {
    props.frameRef.current?.contentWindow?.postMessage({ type: "launchpad-walk-scroll", deltaY }, "*");
  };
  const capture = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    props.onPoint({
      x: Math.round(((event.clientX - rect.left) / Math.max(rect.width, 1)) * 1000) / 10,
      y: Math.round(((event.clientY - rect.top) / Math.max(rect.height, 1)) * 1000) / 10
    });
  };
  return (
    <div
      className={`walk-capture-layer${passthrough ? " passthrough" : ""}`}
      aria-hidden="true"
      onPointerDown={(event) => { touchY.current = event.clientY; capture(event); }}
      onPointerUp={() => { touchY.current = null; }}
      onPointerMove={(event) => {
        capture(event);
        // Touch: dragging the finger scrolls the page under the layer
        // (there is no wheel on a phone).
        if (event.pointerType === "touch" && touchY.current !== null && event.buttons > 0) {
          const delta = touchY.current - event.clientY;
          touchY.current = event.clientY;
          relayScroll(delta);
        }
      }}
      onWheel={(event) => {
        relayScroll(event.deltaY);
        setPassthrough(true);
        window.clearTimeout(passTimer.current);
        passTimer.current = window.setTimeout(() => setPassthrough(false), 700);
      }}
    />
  );
}

export function ReaderApprovalAction(props: {
  placement: "panel" | "end";
  unlocked: boolean;
  progress: number;
  approving: boolean;
  sending: boolean;
  hintKey: string;
  t: (key: string) => string;
  onApprove: () => void;
}) {
  const progress = Math.min(100, Math.max(0, Math.round(props.progress)));
  if (props.placement === "panel" && !props.unlocked) {
    return (
      <div className="reader-approval-pending">
        <p>{props.t("sales.gate.readFirst")}</p>
        <div
          className="reader-approval-progress"
          role="progressbar"
          aria-label={props.t("sales.gate.readProgress").replace("{progress}", String(progress))}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <span className="reader-approval-progress-track" aria-hidden="true">
            <span style={{ width: `${progress}%` }} />
          </span>
          <strong>{props.t("sales.gate.readProgress").replace("{progress}", String(progress))}</strong>
        </div>
      </div>
    );
  }
  return (
    <section className={`reader-approval-action ${props.placement}`} aria-label={props.t("sales.gate.endTitle")} aria-live="polite">
      {props.placement === "end" && <h3>{props.t("sales.gate.endTitle")}</h3>}
      <p className="gate-doc-hint">{props.t(props.hintKey)}</p>
      <button
        className={props.placement === "end" ? "primary-button" : "secondary-button"}
        type="button"
        disabled={!props.unlocked || props.approving || props.sending}
        onClick={() => props.onApprove()}
      >
        {props.approving ? props.t("sales.gate.approving") : props.t("sales.gate.approve")}
      </button>
    </section>
  );
}
